import { useState } from 'react';
import { useLocation } from 'wouter';
import { AlertCircle, Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { getFirebaseAuth } from '@/lib/firebase';

export default function TesterLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setLocation] = useLocation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('[TesterLogin] Sending login request to /api/tester-login');
      console.log('[TesterLogin] Email:', email);
      const response = await fetch('/api/tester-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          password,
        }),
        credentials: 'include',
      });

      const data = await response.json();
      console.log('[TesterLogin] Response:', data);

      if (!response.ok) {
        console.error('[TesterLogin] Login failed:', data);
        setError(data.error || 'Login failed');
        return;
      }

      if (!data.success) {
        console.error('[TesterLogin] Response not successful:', data);
        setError(data.error || 'Login failed');
        return;
      }

      console.log('[TesterLogin] Login successful, tester data:', data.tester);

      // Store tester info in localStorage
      localStorage.setItem('tester_authenticated', 'true');
      localStorage.setItem('tester_uid', data.tester.uid);
      localStorage.setItem('tester_email', data.tester.email);
      localStorage.setItem('tester_name', data.tester.name);

      // Sign in with Firebase custom token (server should have set cookies)
      try {
        getFirebaseAuth();
        // The server has already set the session cookie, so we just need to update local auth state
        // Try to get current user or wait for auth state to update
        console.log('[TesterLogin] Session cookie set by server, auth state should update automatically');
      } catch (firebaseError) {
        console.warn('[TesterLogin] Firebase auth check failed:', firebaseError);
      }

      // Redirect to home
      setLocation('/');
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('[TesterLogin] Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
      {/* Main Content */}
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <div className="w-20 h-20 bg-white flex items-center justify-center border-2 border-white">
            <span className="text-2xl font-bold text-black">PX</span>
          </div>
        </div>

        {/* Title */}
        <div className="text-center space-y-3">
          <h1 className="text-5xl font-bold text-white tracking-tight leading-tight">
            PLAY<span className="text-gray-600">X</span>WORLD
          </h1>
          <p className="text-gray-500 font-mono text-sm tracking-widest">
            Email/Password authentication
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-900/30 border border-red-700/50 text-red-400">
            <AlertCircle size={18} className="flex-shrink-0" />
            <span className="text-sm font-mono">{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          {/* Email Input */}
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              <Mail size={20} strokeWidth={1.5} />
            </div>
            <input
              id="email"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              className="w-full pl-14 pr-4 py-4 bg-transparent border border-gray-700 text-gray-400 placeholder-gray-600 font-mono text-sm focus:outline-none focus:border-gray-500 transition-colors"
            />
          </div>

          {/* Password Input */}
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              <Lock size={20} strokeWidth={1.5} />
            </div>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
              className="w-full pl-14 pr-14 py-4 bg-transparent border border-gray-700 text-gray-400 placeholder-gray-600 font-mono text-sm focus:outline-none focus:border-gray-500 transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 disabled:opacity-50 transition-colors"
            >
              {showPassword ? (
                <EyeOff size={20} strokeWidth={1.5} />
              ) : (
                <Eye size={20} strokeWidth={1.5} />
              )}
            </button>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full py-4 bg-white text-black font-bold text-base tracking-widest hover:bg-gray-200 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="inline mr-2 h-5 w-5 animate-spin" />
                SIGNING IN...
              </>
            ) : (
              <>
                <span className="mr-2">→</span>
                SIGN IN
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="h-px bg-gray-800"></div>

        {/* Footer */}
        <div className="text-center space-y-2">
          <p className="text-gray-600 text-xs font-mono tracking-widest">
            This is a staging environment for testers only
          </p>
          <p className="text-gray-600 text-xs font-mono tracking-widest">
            Unauthorized access is prohibited
          </p>
          <p className="text-gray-700 text-xs font-mono tracking-widest pt-4">
            PlayXworld — Premium Game Top-up Service
          </p>
        </div>
      </div>
    </div>
  );
}
